/**
 * Undo/redo as whole-scene snapshots.
 *
 * A snapshot is an array of POINTERS to frozen immutable elements, so it costs
 * kilobytes and shares every unchanged element with every other snapshot.
 * Nothing here ever deep-clones or stringifies; that discipline is the only
 * reason snapshots are cheap enough to prefer over patches.
 *
 * Undo is a targeted diff-and-reapply, not a state swap: only the ids the action
 * actually changed are restored, so undo cannot revert concurrent edits that
 * arrived from another tab.
 */

import type { EditMeta } from './elements';
import type { SlateElement } from './types';

export interface HistoryEntry {
  readonly elements: readonly SlateElement[];
  readonly selectedIds: readonly string[];
  readonly changedIds: readonly string[];
}

export interface History {
  readonly past: readonly HistoryEntry[];
  readonly future: readonly HistoryEntry[];
  /** Snapshot captured at the start of a gesture or text edit session. */
  readonly pending: { readonly elements: readonly SlateElement[]; readonly selectedIds: readonly string[] } | null;
}

export const MAX_HISTORY = 100;

export const EMPTY_HISTORY: History = { past: [], future: [], pending: null };

export function diffChangedIds(
  before: readonly SlateElement[],
  after: readonly SlateElement[],
): string[] {
  const beforeById = new Map(before.map((el) => [el.id, el] as const));
  const changed: string[] = [];
  const seen = new Set<string>();
  for (const el of after) {
    seen.add(el.id);
    const prev = beforeById.get(el.id);
    if (prev !== el) changed.push(el.id);
  }
  for (const el of before) if (!seen.has(el.id)) changed.push(el.id);
  return changed;
}

export function pushEntry(history: History, entry: HistoryEntry): History {
  if (entry.changedIds.length === 0) return history;
  const past = [...history.past, entry];
  return {
    past: past.length > MAX_HISTORY ? past.slice(past.length - MAX_HISTORY) : past,
    future: [],
    pending: null,
  };
}

/**
 * Restore only `entry.changedIds` onto the current elements.
 *
 * Restored elements get version = current + 1 and a fresh timestamp: an undo is
 * a FORWARD edit as far as last-write-wins is concerned. Reinstating an old
 * version number would make the peer tab reject the change as stale, and the two
 * tabs would diverge with no way back.
 */
export function restoreChanged(
  current: readonly SlateElement[],
  entry: HistoryEntry,
  meta: EditMeta,
): readonly SlateElement[] {
  const entryById = new Map(entry.elements.map((el) => [el.id, el] as const));
  const changed = new Set(entry.changedIds);
  const out: SlateElement[] = [];
  const handled = new Set<string>();

  for (const el of current) {
    if (!changed.has(el.id)) {
      out.push(el);
      continue;
    }
    handled.add(el.id);
    const old = entryById.get(el.id);
    if (old) {
      out.push(
        Object.freeze({
          ...old,
          version: el.version + 1,
          updatedAt: meta.now,
          updatedBy: meta.clientId,
        }) as SlateElement,
      );
    } else {
      // The action created this element; undoing means tombstoning it.
      out.push(
        Object.freeze({
          ...el,
          isDeleted: true,
          version: el.version + 1,
          updatedAt: meta.now,
          updatedBy: meta.clientId,
        }) as SlateElement,
      );
    }
  }

  // Elements the action removed outright rather than tombstoning.
  for (const id of entry.changedIds) {
    if (handled.has(id)) continue;
    const old = entryById.get(id);
    if (!old) continue;
    out.push(
      Object.freeze({ ...old, version: old.version + 1, updatedAt: meta.now, updatedBy: meta.clientId }) as SlateElement,
    );
  }

  return out;
}
